import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { 
  Pencil, 
  AlertCircle, 
  TrendingUp, 
  TrendingDown,
  X
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper: Get last Monday at midnight
const lastMondayMidnight = () => {
  const now = new Date();
  const daysSinceMon = (now.getDay() + 6) % 7; // Mon=0 ... Sun=6
  const d = new Date(now);
  d.setDate(now.getDate() - daysSinceMon);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Format number with K suffix
const formatCompact = (num) => {
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  }
  return num.toString();
};

// Format currency INR
const formatINR = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Mini sparkline SVG
const Sparkline = ({ data, width = 56, height = 18 }) => {
  if (!data || data.length < 2) return null;
  
  const values = data.map(d => d.credits);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * (width - 4) + 2;
    const y = height - 2 - ((d.credits - min) / range) * (height - 4);
    return `${x},${y}`;
  }).join(' ');
  
  const lastPoint = points.split(' ').pop().split(',');
  
  return (
    <svg width={width} height={height} className="inline-block ml-1">
      <polyline
        fill="none"
        stroke="#2EB4AC"
        strokeWidth="1.5"
        points={points}
      />
      <circle
        cx={lastPoint[0]}
        cy={lastPoint[1]}
        r="2"
        fill="#2EB4AC"
      />
    </svg>
  );
};

const CostTrackerWidget = () => {
  const [state, setState] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCredits, setEditCredits] = useState('');
  const [editRate, setEditRate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  
  const pillRef = useRef(null);

  // Fetch data
  const fetchData = async () => {
    try {
      const [stateRes, historyRes] = await Promise.all([
        axios.get(`${API}/cost-tracker`, { withCredentials: true }),
        axios.get(`${API}/cost-tracker/history?weeks=12`, { withCredentials: true })
      ]);
      setState(stateRes.data);
      setHistory(historyRes.data.history || []);
      setEditCredits(stateRes.data.total_credits?.toString() || '0');
      setEditRate(stateRes.data.cost_per_credit_inr?.toString() || '13.70');
      setError(false);
    } catch (err) {
      console.error('Failed to fetch cost tracker:', err);
      setError(true);
      // Set defaults on error
      setState({ total_credits: 0, cost_per_credit_inr: 13.70, updated_at: null });
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Click outside to collapse
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (expanded && pillRef.current && !pillRef.current.contains(e.target)) {
        setExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expanded]);

  // Check if stale
  const isStale = !state?.updated_at || new Date(state.updated_at) < lastMondayMidnight();

  // Calculate totals
  const totalCredits = state?.total_credits || 0;
  const costPerCredit = state?.cost_per_credit_inr || 13.70;
  const totalSpent = totalCredits * costPerCredit;

  // Live preview
  const previewCredits = parseFloat(editCredits) || 0;
  const previewRate = parseFloat(editRate) || 13.70;
  const previewTotal = previewCredits * previewRate;

  // Trend calculation
  const getTrend = () => {
    if (history.length < 2) return null;
    const current = history[history.length - 1]?.credits || 0;
    const previous = history[history.length - 2]?.credits || 0;
    const diff = current - previous;
    const pct = previous > 0 ? ((diff / previous) * 100).toFixed(0) : 0;
    return { diff, pct, up: diff > 0 };
  };
  const trend = getTrend();

  // Handle save
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/cost-tracker`, {
        total_credits: parseFloat(editCredits) || 0,
        cost_per_credit_inr: parseFloat(editRate) || 13.70
      }, { withCredentials: true });
      
      toast.success('Cost tracker updated');
      await fetchData();
      setDialogOpen(false);
      setExpanded(false);
    } catch (err) {
      toast.error('Failed to update cost tracker');
    } finally {
      setSaving(false);
    }
  };

  // Format chart X-axis
  const formatXAxis = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return format(date, 'd MMM');
    } catch {
      return dateStr;
    }
  };

  // Chart data
  const chartData = history.map(h => ({
    ...h,
    label: formatXAxis(h.week_start)
  }));

  if (loading) {
    return (
      <div className="w-10 h-10 rounded-full bg-slate-100 animate-pulse" />
    );
  }

  // Collapsed state - icon only
  if (!expanded && !dialogOpen) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="relative w-10 h-10 rounded-full bg-slate-50 hover:bg-slate-100 flex items-center justify-center transition-colors"
        data-testid="cost-tracker-icon"
      >
        <img 
          src="/icons/cost-rupee.png" 
          alt="Cost" 
          className="h-7 w-7 hover:scale-110 transition-transform"
        />
        {isStale && (
          <>
            <span className="absolute top-0.5 right-0.5 w-3 h-3 bg-rose-500 rounded-full animate-ping opacity-75" />
            <span className="absolute top-0.5 right-0.5 w-3 h-3 bg-rose-500 rounded-full border-2 border-white" />
          </>
        )}
      </button>
    );
  }

  // Expanded state - stat pill
  return (
    <>
      <div
        ref={pillRef}
        onClick={() => setDialogOpen(true)}
        className="h-11 rounded-xl border border-slate-200 bg-white flex items-center cursor-pointer hover:border-slate-300 transition-all animate-in fade-in slide-in-from-right-2 duration-200"
        data-testid="cost-tracker-expanded"
      >
        {/* Left cell - Credits */}
        <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50/50 rounded-l-xl">
          <img src="/icons/cost-rupee.png" alt="₹" className="h-5 w-5" />
          <div className="flex flex-col">
            <span className="text-[9px] font-semibold uppercase text-slate-400 tracking-wider leading-none">Credits</span>
            <span className="text-sm font-bold tabular-nums text-slate-800">{formatCompact(totalCredits)}</span>
          </div>
        </div>
        
        {/* Divider */}
        <div className="w-px h-7 bg-slate-200" />
        
        {/* Right cell - Spent */}
        <div className="flex items-center gap-2 px-3 py-1.5">
          <div className="flex flex-col">
            <span className="text-[9px] font-semibold uppercase text-[#2EB4AC] tracking-wider leading-none">Spent</span>
            <div className="flex items-center">
              <span className="text-sm font-bold tabular-nums text-slate-800">{formatINR(totalSpent)}</span>
              <Sparkline data={history} />
            </div>
          </div>
          {isStale ? (
            <span className="w-2.5 h-2.5 bg-rose-500 rounded-full animate-pulse" />
          ) : (
            <Pencil className="w-3.5 h-3.5 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
          )}
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <img src="/icons/cost-rupee.png" alt="₹" className="h-6 w-6" />
              App Development Cost Tracker
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Stale Alert */}
            {isStale && (
              <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  It's a new week — please update the credits consumed since last Monday.
                </p>
              </div>
            )}

            {/* Trendline Chart */}
            {history.length >= 2 ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Week-on-Week Credits</p>
                    <p className="text-[10px] text-slate-400">Last {history.length} weeks · Mondays</p>
                  </div>
                  {trend && (
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      trend.up ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600'
                    }`}>
                      {trend.up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                      {trend.up ? '+' : ''}{formatCompact(trend.diff)} cr ({trend.up ? '+' : ''}{trend.pct}%)
                    </div>
                  )}
                </div>
                
                <div className="h-44 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id="creditGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#2EB4AC" stopOpacity={0.35} />
                          <stop offset="95%" stopColor="#2EB4AC" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="label" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fontSize: 10, fill: '#94a3b8' }}
                      />
                      <YAxis 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fontSize: 10, fill: '#94a3b8' }}
                        tickFormatter={formatCompact}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          borderRadius: 12, 
                          border: '1px solid #e2e8f0',
                          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                        }}
                        formatter={(value) => [formatCompact(value) + ' credits', 'Credits']}
                        labelFormatter={(label) => `Week of ${label}`}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="credits" 
                        stroke="#2EB4AC" 
                        strokeWidth={2}
                        fill="url(#creditGradient)"
                        dot={{ r: 3, fill: 'white', stroke: '#2EB4AC', strokeWidth: 2 }}
                        activeDot={{ r: 5, fill: '#2EB4AC' }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                <p className="text-sm text-slate-500">Save updates each week to build the trendline.</p>
              </div>
            )}

            {/* Input Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700">Total Credits Consumed</label>
                <Input
                  type="number"
                  value={editCredits}
                  onChange={(e) => setEditCredits(e.target.value)}
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700">Cost per Credit (₹)</label>
                <Input
                  type="number"
                  value={editRate}
                  onChange={(e) => setEditRate(e.target.value)}
                  placeholder="13.70"
                  min="0"
                  step="0.01"
                />
                <p className="text-[10px] text-slate-400">Default ₹13.70 — edit anytime as billing changes.</p>
              </div>
            </div>

            {/* Live Preview */}
            <div className="bg-slate-50/60 rounded-xl p-4 text-center">
              <p className="text-xs font-medium text-slate-500 mb-1">Estimated Total Cost</p>
              <p className="text-3xl font-bold text-slate-900">{formatINR(previewTotal)}</p>
            </div>

            {/* Last Updated */}
            {state?.updated_at && (
              <p className="text-xs text-slate-400 text-center">
                Last updated {format(new Date(state.updated_at), 'PPp')} by {state.updated_by?.split('@')[0] || 'unknown'}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-[#2EB4AC] hover:bg-[#259993] text-white"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CostTrackerWidget;
